import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import reportService from "../services/report.service";
import mediaClientService from "../services/mediaClient.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";

const StorageSize = () => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [storageData, setStorageData] = useState(null);
  const [error, setError] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);

  const { user: currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!currentUser) return;

    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await mediaClientService.getParentClients();
        setClients(response.data || []);
      } catch (err) {
        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load clients" });
        console.error(err);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (val.trim() === "") {
      setSelectedClientId("");
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      searchClients();
    }
  };

  const searchClients = () => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      toast.warning("Please enter a client name to search.");
      return;
    }

    if (searchTerm.trim().length < 3) {
      toast.warning("Search term must be at least 3 characters long.");
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = clients.filter(
      (client) =>
        (client.clientName && client.clientName.toLowerCase().includes(term)) ||
        (client.mediaClientId && client.mediaClientId.toLowerCase().includes(term))
    );

    setFilteredClients(filtered);

    if (filtered.length === 1) {
      selectClient(filtered[0].mediaClientId, filtered[0].clientName);
    } else if (filtered.length === 0) {
      toast.warning("No clients found matching that name.");
    } else {
      setShowModal(true);
    }
  };

  const selectClient = (clientId, clientName) => {
    setSelectedClientId(clientId);
    setSearchTerm(clientName || clientId);
    setShowModal(false);
  };

  const handleCalculate = async () => {
    if (!selectedClientId) {
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please select a client" });
      return;
    }

    setLoading(true);
    setError(null);
    setStorageData(null);
    try {
      const response = await reportService.getStorageSize(selectedClientId);
      setStorageData(response.data);
    } catch (err) {
      const errorMessage =
        (err.response && err.response.data) ||
        err.message ||
        "Failed to calculate storage size";
      setError(errorMessage);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to calculate storage size" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatGB = (gb) => {
    if (gb === null || gb === undefined) return "—";
    return gb.toFixed(2);
  };

  const selectedClient = clients.find((c) => c.mediaClientId === selectedClientId);

  return (
    <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
      <div className="row">
        <div className="col-12">
          <h2>Storage Size</h2>
          <p className="text-muted">
            Calculate S3 bucket storage size for a selected client
          </p>
        </div>
      </div>

      <div className="row mt-3 mb-3 align-items-end">
        <div className="col-12 col-md-6">
          <label htmlFor="clientSearch" className="form-label">
            Search Client:
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="clientSearch"
              placeholder="Enter client name (min 3 chars)..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              disabled={loadingClients}
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={searchClients}
              disabled={loadingClients || searchTerm.trim().length < 3}
            >
              {loadingClients ? (
                <span className="spinner-border spinner-border-sm" role="status"></span>
              ) : (
                "Search"
              )}
            </button>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <button
            className="btn btn-primary w-100"
            onClick={handleCalculate}
            disabled={loading || !selectedClientId}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Calculating...
              </>
            ) : (
              "Calculate"
            )}
          </button>
        </div>
      </div>

      {selectedClientId && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="form-text text-success mt-0">
              Selected client ID: <strong>{selectedClientId}</strong>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center mt-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-4" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && storageData && (
        <div className="row mt-4">
          <div className="col-12">
            {selectedClient && (
              <div className="card mb-3">
                <div className="card-header bg-white border-0 py-3 px-4 fw-bold text-dark fs-5">
                  <h5 className="mb-0">Client: {selectedClient.clientName || selectedClientId}</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-2 text-muted">Media Size</h6>
                          <h4 className="card-title">{formatGB(storageData.mediaSizeGB)} GB</h4>
                          <small className="text-muted">public/Media/</small>
                          <div className="mt-2">
                            <small className="text-muted">Objects: {storageData.mediaObjectCount?.toLocaleString() || 0}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                    {storageData.inventorySizeGB !== null && (
                      <div className="col-md-4">
                        <div className="card bg-light">
                          <div className="card-body">
                            <h6 className="card-subtitle mb-2 text-muted">Inventory Size</h6>
                            <h4 className="card-title">{formatGB(storageData.inventorySizeGB)} GB</h4>
                            <small className="text-muted">public/Inventory/</small>
                            <div className="mt-2">
                              <small className="text-muted">Objects: {storageData.inventoryObjectCount?.toLocaleString() || 0}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="col-md-4">
                      <div className="card bg-primary text-white">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-2">Total Size</h6>
                          <h4 className="card-title">{formatGB(storageData.totalSizeGB)} GB</h4>
                          <div className="mt-2">
                            <small>Objects: {storageData.totalObjectCount?.toLocaleString() || 0}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Client Selection Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Client</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="list-group">
                  {filteredClients.map((client) => (
                    <button
                      key={client.mediaClientId}
                      className="list-group-item list-group-item-action"
                      onClick={() => selectClient(client.mediaClientId, client.clientName)}
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1">{client.clientName || client.mediaClientId}</h6>
                      </div>
                      <small className="text-muted">Client ID: {client.mediaClientId}</small>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorageSize;

